import { useEffect } from "react";
import type { ReactNode } from "react";

import i18n from "@/i18n";
import { useLanguages } from "@/hooks/useLanguages";

/** Fallback for locales not yet present in the registry (e.g. anonymous pages). */
const FALLBACK_RTL_LOCALES = new Set(["ar", "he", "fa", "ur"]);

/**
 * Applies `lang` and `dir` to the document element from the active i18n
 * locale. Direction comes from the language registry's `direction` column
 * (the single source of truth for layout mirroring), falling back to a static
 * RTL set while anonymous or before the registry loads (README section 0.8).
 */
export function DirectionProvider({ children }: { children: ReactNode }) {
  const languages = useLanguages();

  useEffect(() => {
    const apply = () => {
      const lang = (i18n.language ?? "en").split("-")[0];
      document.documentElement.lang = lang;
      const registry = languages.data?.find((language) => language.code === lang);
      const dir = registry
        ? registry.direction
        : FALLBACK_RTL_LOCALES.has(lang)
          ? "rtl"
          : "ltr";
      document.documentElement.dir = dir;
    };
    apply();
    i18n.on("languageChanged", apply);
    return () => {
      i18n.off("languageChanged", apply);
    };
  }, [languages.data]);

  return <>{children}</>;
}
